import React, { useCallback, useMemo, useState } from 'react';
import ReactTooltip from 'react-tooltip';
import LayoutDashboard from '../../layout';
import { Root } from '../users/index.styled';
import MaterialTable from '../../../../components/table';
import { transactions as types } from '../../../../types';
import columns from './index.columns';
import DialogApprove from './dialogs/apporve';
import DialogReject from './dialogs/reject';
import { toast } from 'react-toastify';
import AppFormikInput from '../../../../components/form/formik/input/input';
import { Formik } from 'formik';
import { Col, Row } from 'components/layout/layout';
import { EErrorPos, ELabelPos } from '../../../../components/form/formik/types';
import ButtonFilterApply from './components/button-filter-apply';
import ButtonFilterClear from './components/button-filter-clear';
import { Input } from 'components/input/input/input';
import LabelWrapper from '../../../../components/label-wrapper';
import { ETransactionType } from '../../../../types/entities/transactions';
import { useInfiniteQuery } from 'react-query';
import { servicesTransactions as services } from '../../../../services';
import { useInView } from 'react-intersection-observer';
import {
    EButtonSize,
    EButtonType,
} from '../../../../components/control/button/button.types';
import Button from '../../../../components/control/button/button';
import wrapUrlWithServerUrl from '../../../../utils/fetch/utils/wrap-url-with-server-url';
import endpoints, { EEndpoints } from '../../../../config/endpoints';
import { setQueryParamsObjToString } from '../../../../utils/query-params-transfer';
import useAppBarTitle from 'hooks/use-app-bar-title';

type TTransactionsHistory = {};

export type TFilterValues = {
    dateFrom?: string;
    dateTo?: string;
};

const filterValues = {
    dateTo: '',
    dateFrom: '',
};

const pageTitle = 'Transactions History';

const TransactionsHistory: React.FC<TTransactionsHistory> = (props) => {
    useAppBarTitle(pageTitle);
    const [isOnConfirmDialogOpen, setIsOnConfirmDialogOpen] =
        useState<boolean>(false);
    const [isOnRejectDialogOpen, setIsOnRejectDialogOpen] =
        useState<boolean>(false);
    const [curData, setCurData] = useState<types.TTransactions>();
    const [filterFromValue, setFilterFormValues] =
        useState<TFilterValues>(filterValues);
    const [searchNexId, setSearchNexId] = useState<string>();
    const [searchRefNo, setSearchRefNo] = useState<string>();
    const [filterByTransType, setFilterByTransType] = useState<string>('');
    const [filterByTransStatus, setFilterByTransStatus] = useState<string>('');
    const [orderBy, setOrderBy] = useState<string>('created_at');
    const [orderAsc, setOrderAsc] = useState<boolean>(false);
    const filters = {
        fromDate: filterFromValue.dateFrom,
        toDate: filterFromValue.dateTo,
        status: filterByTransStatus,
        type: filterByTransType,
        nexId: searchNexId,
        referenceNo: searchRefNo,
        orderBy,
        orderAsc,
    };
    const [queryParams, setQueryParams] = React.useState<string>(
        setQueryParamsObjToString(filters, true),
    );
    const { data, isLoading, refetch, fetchMore, isFetchingMore } =
        useInfiniteQuery(['transactions', filters], services.get, {
            getFetchMore: (lastGroup, allGroups) => {
                return lastGroup.nextPage;
            }, // nextPage because we named it nextPage
        });

    const canFetchMore = data && data[data.length - 1].data.length > 0;

    const handleOnSuccessMutation = useCallback(() => {
        refetch();
    }, [refetch]);

    const columnsFinal = useMemo(
        () =>
            columns({
                handlers: {
                    confirm: (data) => {
                        setCurData(data);
                        setIsOnConfirmDialogOpen(true);
                    },
                    reject: (data) => {
                        setCurData(data);
                        setIsOnRejectDialogOpen(true);
                    },
                },
            }),
        [],
    );

    let finalData = [];
    if (data) {
        data.forEach((item) => {
            finalData = [...finalData, ...item.data];
        });
    }

    const downloadFile = async () => {
        const url = wrapUrlWithServerUrl(
            `${endpoints[
                EEndpoints.transactionsGet
            ]()}/download?${queryParams}`,
        );
        window.open(url, '_blank');
    };

    const { ref, inView } = useInView();
    React.useEffect(() => {
        if (inView && canFetchMore && data) {
            const fetchMoreVariable = data[data.length - 1].nextPage;
            fetchMore(fetchMoreVariable);
        }
    }, [inView]);

    React.useEffect(() => {
        const newParam = {
            fromDate: filterFromValue.dateFrom,
            toDate: filterFromValue.dateTo,
            status: filterByTransStatus,
            type: filterByTransType,
            nexId: searchNexId,
            referenceNo: searchRefNo,
            orderBy,
            orderAsc,
        };
        setQueryParams(setQueryParamsObjToString(newParam, true));
    }, [
        filterFromValue,
        searchNexId,
        searchRefNo,
        filterByTransType,
        filterByTransStatus,
    ]);

    return (
        <LayoutDashboard>
            <Root>
                <Row justify={'start'} gap={3}>
                    <Col className={'rowrow'} justify={'start'}>
                        <Col>
                            <LabelWrapper
                                label={'Search by NEX ID'}
                                labelPos={ELabelPos.top}
                            >
                                <Input
                                    value={searchNexId}
                                    onChange={(e) => {
                                        const curVal = e.target.value;
                                        setSearchNexId(curVal);
                                    }}
                                />
                            </LabelWrapper>
                        </Col>
                        <Col>
                            <LabelWrapper
                                label={'Search by Reference No'}
                                labelPos={ELabelPos.top}
                            >
                                <Input
                                    value={searchRefNo}
                                    onChange={(e) => {
                                        const curVal = e.target.value;
                                        setSearchRefNo(curVal);
                                    }}
                                />
                            </LabelWrapper>
                        </Col>
                        <Formik
                            enableReinitialize={true}
                            initialValues={filterFromValue}
                            onSubmit={(values) => {
                                setFilterFormValues(values);
                            }}
                            validate={(values) => {
                                let errors: Partial<TFilterValues> = {};

                                if (!values.dateFrom && values.dateTo) {
                                    errors.dateFrom =
                                        'Required if "To date" selected';
                                }

                                if (values.dateFrom && !values.dateTo) {
                                    errors.dateTo =
                                        'Required if "From date" selected';
                                }

                                return errors;
                            }}
                        >
                            <Col align={'bottom'} justify={'start'}>
                                <AppFormikInput
                                    id={'dateFrom'}
                                    label={'Date from'}
                                    errorPos={EErrorPos.bottom}
                                    labelPos={ELabelPos.top}
                                    name={'dateFrom'}
                                    inputProps={{ type: 'date' }}
                                />
                                <AppFormikInput
                                    id={'dateTo'}
                                    labelPos={ELabelPos.top}
                                    errorPos={EErrorPos.bottom}
                                    label={'Date to'}
                                    name={'dateTo'}
                                    inputProps={{ type: 'date' }}
                                />
                                <ButtonFilterApply />
                                <ButtonFilterClear
                                    onClear={() => {
                                        const dateTo: HTMLInputElement | null =
                                            document.getElementById(
                                                'dateTo',
                                            ) as HTMLInputElement;
                                        if (dateTo) {
                                            dateTo['valueAsDate'] = null;
                                        }
                                        const dateFrom: HTMLInputElement | null =
                                            document.getElementById(
                                                'dateFrom',
                                            ) as HTMLInputElement;
                                        if (dateFrom) {
                                            dateFrom['valueAsDate'] = null;
                                        }
                                        setFilterFormValues(filterValues);
                                    }}
                                />
                            </Col>
                        </Formik>
                        <Col>
                            <LabelWrapper
                                label={'Filter by transaction type'}
                                labelPos={ELabelPos.top}
                            >
                                <select
                                    defaultValue={''}
                                    value={filterByTransType}
                                    onChange={(e) => {
                                        if (e?.target?.value) {
                                            setFilterByTransType(
                                                e.target.value,
                                            );
                                        }
                                    }}
                                >
                                    <option value={'0'}>Top up</option>
                                    {/*<option value={'1'}>Debit</option>*/}
                                    <option value={'2'}>Direct debit</option>
                                    <option value={'3'}>Withdraw</option>
                                    <option value={'4'}>Wallet</option>
                                    <option value={'6'}>Interbank</option>
                                    <option value={'7'}>Payment</option>
                                    {/*<option value={'5'}>Pulsa</option>*/}
                                    <option value={''}>All</option>
                                </select>
                            </LabelWrapper>
                            <LabelWrapper
                                label={'Filter by transaction status'}
                                labelPos={ELabelPos.top}
                            >
                                <select
                                    defaultValue={''}
                                    value={filterByTransStatus}
                                    onChange={(e) => {
                                        if (e?.target?.value) {
                                            setFilterByTransStatus(
                                                e.target.value,
                                            );
                                        }
                                    }}
                                >
                                    <option value={'0'}>Pending</option>
                                    <option value={'1'}>Rejected</option>
                                    <option value={'2'}>Completed</option>
                                    <option value={''}>All</option>
                                </select>
                            </LabelWrapper>
                        </Col>
                        <Col align={'bottom'} justify={'end'}>
                            {/*<a > ◕‿‿◕ </a>*/}
                            <Button
                                data-tip="
                              Clicking this button will download the transactions based on the current filter.
                              If no filters are applied, it will download all transactions from the past 3 months.
                              "
                                styleType={EButtonType.Outline}
                                size={EButtonSize.Normal}
                                onClick={downloadFile}
                            >
                                Export as CSV
                            </Button>
                            <ReactTooltip
                                place="bottom"
                                type="dark"
                                effect="solid"
                            >
                                <p>
                                    Clicking this button will download the
                                    transactions based on the current filter.
                                </p>
                                <p>
                                    If no filters are applied, it will download
                                    all transactions from the past 3 months.
                                </p>
                            </ReactTooltip>
                        </Col>
                    </Col>
                    <Col align={'bottom'} justify={'end'}>
                        {finalData && (
                            <MaterialTable<types.TTransactions>
                                data={finalData}
                                columns={columnsFinal}
                                isLoading={isLoading}
                                setOrderBy={setOrderBy}
                                setOrderAsc={setOrderAsc}
                                orderBy={orderBy}
                                orderAsc={orderAsc}
                            />
                        )}
                        {data && (
                            <button
                                ref={ref}
                                onClick={() =>
                                    fetchMore(data[data.length - 1].nextPage)
                                }
                                disabled={!canFetchMore || isFetchingMore}
                            >
                                {isFetchingMore
                                    ? 'Loading more...'
                                    : canFetchMore
                                    ? 'Load Newer'
                                    : 'Nothing more to load'}
                            </button>
                        )}
                    </Col>
                </Row>
            </Root>
            {curData && (
                <>
                    <DialogApprove
                        asyncStatesHandlers={{
                            onSuccess: () => {
                                toast.success('Successfully approved');
                                handleOnSuccessMutation();
                            },
                            onError: (e) => {
                                toast.error(`Error: ${e}`);
                                handleOnSuccessMutation();
                            },
                        }}
                        curData={curData}
                        isDialogOpen={isOnConfirmDialogOpen}
                        setIsDialogOpen={setIsOnConfirmDialogOpen}
                    />
                    {curData.type !== ETransactionType.PULSA && (
                        <DialogReject
                            asyncStatesHandlers={{
                                onSuccess: () => {
                                    toast.success('Successfully rejected');
                                    handleOnSuccessMutation();
                                },
                                onError: (e) => {
                                    toast.error(`Error: ${e}`);
                                    handleOnSuccessMutation();
                                },
                            }}
                            curData={curData}
                            isDialogOpen={isOnRejectDialogOpen}
                            setIsDialogOpen={setIsOnRejectDialogOpen}
                        />
                    )}
                </>
            )}
        </LayoutDashboard>
    );
};

export default TransactionsHistory;
